import { Add, ArrowDown, ArrowUp, TrashCan } from '@carbon/icons-react';
import {
  Button,
  Checkbox,
  FormGroup,
  InlineNotification,
  Layer,
  MenuButton,
  MenuItem,
  Stack,
  Tag,
  TextAreaSkeleton,
} from '@carbon/react';
import { FieldInput, useAdmin, type FieldDef, type FieldEditorProps } from '@pos/admin-ui';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { lazy, Suspense } from 'react';
import { ImageField } from '../images/ImagePicker';
import {
  BLOCK_TYPES,
  type ContentField,
  FORM_FIELDS,
  type Json,
  move,
  newBlock,
  parseContent,
  PROMOTION_FIELDS,
  SITE_FIELDS,
} from './contentModel';
import { FormPicker } from './FormPicker';
import './content.scss';

// The rich text editor (Tiptap) loads with the first rich text block, not with HO admin.
const RichTextEditor = lazy(() => import('./RichTextEditor').then((m) => ({ default: m.RichTextEditor })));

/**
 * A page's or the site settings' content (ADR 0081), edited as fields instead of JSON: the site settings' links, social and legal
 * details, or a page's blocks (add, reorder, remove, each with its own fields). Saving keeps a draft; publishing is its own action.
 */
export function PageContentField(props: FieldEditorProps) {
  const content = parseContent(props.value);
  const save = (next: Json) => props.onChange(JSON.stringify(next));
  if (props.values.kind === 'site') {
    return <Fields fields={SITE_FIELDS} value={content} onChange={save} path="site" formValues={props.values} />;
  }

  // A form (CMS phase 2): its fields, button, text once sent and consent; pages show it with a form block.
  if (props.values.kind === 'form') {
    return (
      <Stack gap={5}>
        {props.invalidText && <InlineNotification kind="error" lowContrast hideCloseButton title={props.invalidText} />}
        <Fields fields={FORM_FIELDS} value={content} onChange={save} path="form" formValues={props.values} />
      </Stack>
    );
  }

  const blocks = <BlockList value={content} onChange={save} formValues={props.values} invalidText={props.invalidText} />;
  return props.values.kind === 'promotion' ? (
    <Stack gap={7}>
      <PromotionDetails value={content} onChange={save} formValues={props.values} />
      {blocks}
    </Stack>
  ) : (
    blocks
  );
}

/** A promotion's details (CMS phase 2): when it runs, for which ways of ordering, its deal and terms; the website filters by them. */
function PromotionDetails({ value, onChange, formValues }: { value: Json; onChange(next: Json): void; formValues: FieldEditorProps['values'] }) {
  const { t } = useTranslation();
  const promotion = (value.promotion && typeof value.promotion === 'object' ? value.promotion : {}) as Json;
  return (
    <FormGroup legendText={t('cms.promotion.title')}>
      <Fields fields={PROMOTION_FIELDS} value={promotion} onChange={(next) => onChange({ ...value, promotion: next })} path="promotion" formValues={formValues} />
    </FormGroup>
  );
}

function BlockList({
  value,
  onChange,
  formValues,
  invalidText,
}: {
  value: Json;
  onChange(next: Json): void;
  formValues: FieldEditorProps['values'];
  invalidText?: string;
}) {
  const { t } = useTranslation();
  const blocks = Array.isArray(value.blocks) ? (value.blocks as Json[]) : [];
  const set = (next: Json[]) => onChange({ ...value, blocks: next });
  return (
    <FormGroup legendText={t('cms.blocks.title')}>
      <Stack gap={5}>
        {invalidText && <InlineNotification kind="error" lowContrast hideCloseButton title={invalidText} />}
        {blocks.map((block, i) => {
          const type = String(block.type ?? '');
          return (
            <Layer key={String(block.id ?? i)}>
              <div className="pos-content__block">
                <div className="pos-content__block-head">
                  <Tag type="cool-gray">{t(`cms.blocks.types.${type}` as I18nKey, { defaultValue: type })}</Tag>
                  <span className="pos-content__block-id">{String(block.id ?? '')}</span>
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={ArrowUp}
                    iconDescription={t('cms.blocks.up')}
                    disabled={i === 0}
                    onClick={() => set(move(blocks, i, i - 1))}
                  />
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={ArrowDown}
                    iconDescription={t('cms.blocks.down')}
                    disabled={i === blocks.length - 1}
                    onClick={() => set(move(blocks, i, i + 1))}
                  />
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={TrashCan}
                    iconDescription={t('cms.blocks.remove')}
                    onClick={() => set(blocks.filter((_, k) => k !== i))}
                  />
                </div>
                <Fields
                  fields={BLOCK_TYPES[type] ?? []}
                  value={block}
                  onChange={(next) => set(blocks.map((b, k) => (k === i ? next : b)))}
                  path={String(block.id ?? i)}
                  formValues={formValues}
                />
              </div>
            </Layer>
          );
        })}
        <div>
          <MenuButton label={t('cms.blocks.add')} kind="tertiary" size="md" menuAlignment="bottom-start">
            {Object.keys(BLOCK_TYPES).map((type) => (
              <MenuItem
                key={type}
                label={t(`cms.blocks.types.${type}` as I18nKey, { defaultValue: type })}
                onClick={() =>
                  set([
                    ...blocks,
                    newBlock(
                      type,
                      blocks.map((b) => String(b.id ?? '')),
                    ),
                  ])
                }
              />
            ))}
          </MenuButton>
        </div>
      </Stack>
    </FormGroup>
  );
}

/** A set of fields of one object (a block, a list item, the site settings), by their descriptions. */
function Fields({
  fields,
  value,
  onChange,
  path,
  formValues,
}: {
  fields: readonly ContentField[];
  value: Json;
  onChange(next: Json): void;
  path: string;
  formValues: FieldEditorProps['values'];
}) {
  const { t } = useTranslation();
  const setField = (key: string, v: unknown) => onChange({ ...value, [key]: v === '' || v === null ? undefined : v });
  return (
    <Stack gap={4}>
      {fields.map((f) => {
        const id = `${path}-${f.key}`;
        const label = `cms.fields.${f.key}` as I18nKey;
        const def = (type: FieldDef['type'], extra: Partial<FieldDef> = {}): FieldDef => ({
          key: id,
          label,
          type,
          required: f.required,
          ...extra,
        });
        switch (f.kind) {
          case 'text':
            return (
              <FieldInput
                key={f.key}
                field={def('localized')}
                value={value[f.key] ?? {}}
                onChange={(v) => setField(f.key, v)}
              />
            );
          case 'richtext':
            return (
              <RichText
                key={f.key}
                id={id}
                label={t(label)}
                value={(value[f.key] ?? {}) as Record<string, string>}
                brandId={typeof formValues.brandId === 'string' && formValues.brandId ? formValues.brandId : null}
                onChange={(v) => setField(f.key, v)}
              />
            );
          case 'plain':
          case 'link':
            return (
              <FieldInput
                key={f.key}
                field={def('text', f.kind === 'link' ? { helper: 'cms.fields.linkHelp' } : {})}
                value={value[f.key] ?? ''}
                onChange={(v) => setField(f.key, v)}
              />
            );
          case 'number':
            return (
              <FieldInput
                key={f.key}
                field={def('number')}
                value={value[f.key] ?? null}
                onChange={(v) => setField(f.key, v)}
              />
            );
          case 'select':
            return (
              <FieldInput
                key={f.key}
                field={def('select', {
                  options: (f.options ?? []).map((o) => ({ value: o, label: `cms.options.${f.key}.${o}` as I18nKey })),
                })}
                value={value[f.key] ?? ''}
                onChange={(v) => setField(f.key, v)}
              />
            );
          case 'boolean':
            return (
              <Checkbox
                key={f.key}
                id={id}
                labelText={t(label)}
                checked={value[f.key] === true}
                onChange={(_, { checked }) => setField(f.key, checked || undefined)}
              />
            );
          case 'form':
            return (
              <FormPicker
                key={f.key}
                id={id}
                label={t(label)}
                brandId={typeof formValues.brandId === 'string' ? formValues.brandId : ''}
                value={typeof value[f.key] === 'string' ? (value[f.key] as string) : ''}
                onChange={(v) => setField(f.key, v)}
              />
            );
          case 'category':
          case 'deal':
            return (
              <FieldInput
                key={f.key}
                field={def('reference', { ref: f.kind === 'deal' ? 'catalog.deal' : 'catalog.item_category' })}
                value={value[f.key] ?? null}
                values={formValues}
                onChange={(v) => setField(f.key, v)}
              />
            );
          case 'multi': {
            // A set of choices (days, channels) as Carbon checkboxes; kept as an array, numbers for the days.
            const chosen = Array.isArray(value[f.key]) ? (value[f.key] as unknown[]).map(String) : [];
            return (
              <FormGroup key={f.key} legendText={t(label)}>
                <div className="pos-content__choices">
                  {(f.options ?? []).map((o) => (
                    <Checkbox
                      key={o}
                      id={`${id}-${o}`}
                      labelText={t(`cms.options.${f.key}.${o}` as I18nKey)}
                      checked={chosen.includes(o)}
                      onChange={(_, { checked }) => {
                        const next = (f.options ?? []).filter((x) => (x === o ? checked : chosen.includes(x)));
                        setField(f.key, next.length ? next.map((x) => (f.numeric ? Number(x) : x)) : undefined);
                      }}
                    />
                  ))}
                </div>
              </FormGroup>
            );
          }
          case 'image':
            return (
              <ImageField
                key={f.key}
                field={def('text')}
                value={value[f.key] ?? ''}
                values={formValues}
                onChange={(v) => setField(f.key, v)}
              />
            );
          case 'group':
            return (
              <FormGroup key={f.key} legendText={t(label)}>
                <Fields
                  fields={f.fields ?? []}
                  value={(value[f.key] ?? {}) as Json}
                  onChange={(v) => setField(f.key, v)}
                  path={id}
                  formValues={formValues}
                />
              </FormGroup>
            );
          case 'list': {
            const items = Array.isArray(value[f.key]) ? (value[f.key] as Json[]) : [];
            const setItems = (next: Json[]) => setField(f.key, next);
            return (
              <FormGroup key={f.key} legendText={t(label)}>
                <Stack gap={4}>
                  {items.map((item, i) => (
                    <div key={i} className="pos-content__item">
                      <div className="pos-content__block-head">
                        <span className="pos-content__block-id">{i + 1}</span>
                        <Button
                          kind="ghost"
                          size="sm"
                          hasIconOnly
                          renderIcon={ArrowUp}
                          iconDescription={t('cms.blocks.up')}
                          disabled={i === 0}
                          onClick={() => setItems(move(items, i, i - 1))}
                        />
                        <Button
                          kind="ghost"
                          size="sm"
                          hasIconOnly
                          renderIcon={ArrowDown}
                          iconDescription={t('cms.blocks.down')}
                          disabled={i === items.length - 1}
                          onClick={() => setItems(move(items, i, i + 1))}
                        />
                        <Button
                          kind="ghost"
                          size="sm"
                          hasIconOnly
                          renderIcon={TrashCan}
                          iconDescription={t('cms.blocks.remove')}
                          onClick={() => setItems(items.filter((_, k) => k !== i))}
                        />
                      </div>
                      <Fields
                        fields={f.fields ?? []}
                        value={item}
                        onChange={(next) => setItems(items.map((x, k) => (k === i ? next : x)))}
                        path={`${id}-${i}`}
                        formValues={formValues}
                      />
                    </div>
                  ))}
                  <div>
                    <Button
                      kind="ghost"
                      size="md"
                      renderIcon={Add}
                      disabled={items.length >= (f.max ?? 50)}
                      onClick={() => setItems([...items, {}])}
                    >
                      {t('cms.blocks.addItem')}
                    </Button>
                  </div>
                </Stack>
              </FormGroup>
            );
          }
        }
      })}
    </Stack>
  );
}

/** Rich text in each of the brand's languages, each in its own editor (headings, lists, links, images, tables...). */
function RichText({
  id,
  label,
  value,
  brandId,
  onChange,
}: {
  id: string;
  label: string;
  value: Record<string, string>;
  brandId: string | null;
  onChange(next: Record<string, string>): void;
}) {
  const { textLanguages } = useAdmin();
  const languages = textLanguages ?? ['vi', 'en'];
  return (
    <FormGroup legendText={label}>
      <Suspense fallback={<TextAreaSkeleton />}>
        <Stack gap={5}>
          {languages.map((code) => (
            <RichTextEditor
              key={code}
              id={`${id}-${code}`}
              label={new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code}
              value={value[code] ?? ''}
              brandId={brandId}
              onChange={(markdown) => onChange({ ...value, [code]: markdown })}
            />
          ))}
        </Stack>
      </Suspense>
    </FormGroup>
  );
}
